"use client";

import React, { useState } from 'react';
import { SeoData, PillarSection } from '../utils/seo';
import { ChevronRightIcon } from '../Icons';

interface PillarContentProps {
  seoData: SeoData;
}

const FAQItem = ({ question, answer }: { question: string, answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full p-4 text-left bg-stone-50 hover:bg-stone-100 transition-colors"
      >
        <span className="font-bold text-stone-800">{question}</span>
        <ChevronRightIcon className={`w-5 h-5 text-stone-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="p-4 bg-white text-stone-600 border-t border-stone-200">
          {answer}
        </div>
      )}
    </div>
  );
};

export const PillarContent: React.FC<PillarContentProps> = ({ seoData }) => {
  if (!seoData.isPillar || !seoData.pillarContent || seoData.pillarContent.length === 0) {
    return null;
  }

  return (
    <div className="mt-16 space-y-12">
      {seoData.pillarContent.map((section, index) => (
        <section key={index} className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 sm:p-8">
          <h2 className="text-2xl sm:text-3xl font-serif text-brand-text-title mb-6">{section.title}</h2>
          
          {section.content && (
            <div 
              className="prose prose-stone max-w-none text-brand-text-body mb-8"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          )}

          {section.type === 'how-to' && section.howToSteps && (
            <div className="space-y-6">
              {section.howToSteps.map((step, stepIdx) => (
                <div key={stepIdx} className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-pink/10 flex items-center justify-center text-brand-pink font-bold">
                    {stepIdx + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-stone-800 mb-1">{step.name}</h3>
                    <p className="text-stone-600">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {section.type === 'faq' && section.faqItems && (
            <div className="space-y-4">
              {section.faqItems.map((faq, faqIdx) => (
                <FAQItem key={faqIdx} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};
